import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isElectron } from '../utils/platform';
import { checkDeviceReachability } from '../utils/deviceProbe';
import { IoTDevice } from '../pages/ZonesDevices';

export interface DeviceHealth {
  status: 'online' | 'offline' | 'checking';
  latency?: number;
  checkedAt?: Date;
  source?: 'local' | 'public';
}

interface UseDeviceHealthCheckOptions {
  interval?: number; // poll interval in ms (default 15000)
}

interface UseDeviceHealthCheckResult {
  healthMap: Map<string, DeviceHealth>;
  isChecking: boolean;
  checkNow: () => void;
}

// Number of consecutive failures before the indicator flips to 'offline'.
// Absorbs single-poll blips without visible flickering.
const OFFLINE_THRESHOLD = 2;

export const useDeviceHealthCheck = (
  devices: IoTDevice[],
  options: UseDeviceHealthCheckOptions = {}
): UseDeviceHealthCheckResult => {
  const { interval = 15000 } = options;

  const [healthMap, setHealthMap] = useState<Map<string, DeviceHealth>>(new Map());
  const [isChecking, setIsChecking] = useState(false);

  const intervalRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const devicesRef       = useRef<IoTDevice[]>(devices);
  // Synchronous mirror of healthMap so we can read it inside async callbacks
  const healthMapRef     = useRef<Map<string, DeviceHealth>>(new Map());
  // Per-device consecutive failure counter for hysteresis
  const failureCountRef  = useRef<Map<string, number>>(new Map());

  devicesRef.current = devices;

  const runCheck = useCallback(async () => {
    if (!isElectron()) return;

    const checkable = devicesRef.current.filter(d => d.local_ip_address || d.ip_address);
    if (checkable.length === 0) return;

    setIsChecking(true);

    // For devices seen before, keep their last known status while re-checking (no flash).
    // For brand-new devices, show 'checking' spinner.
    setHealthMap(prev => {
      const next = new Map(prev);
      for (const d of checkable) {
        if (!prev.get(d.id)?.checkedAt) {
          next.set(d.id, { status: 'checking' });
        }
      }
      healthMapRef.current = next;
      return next;
    });

    // Check all devices in parallel; update each entry as it resolves (no waiting for all)
    await Promise.all(
      checkable.map(async (device) => {
        const { reachable, source, latencyMs } = await checkDeviceReachability({
          local_ip_address: device.local_ip_address,
          ip_address: device.ip_address,
          port: device.port,
        });

        const prevFailures = failureCountRef.current.get(device.id) ?? 0;
        let newHealth: DeviceHealth;

        if (reachable) {
          failureCountRef.current.set(device.id, 0);
          newHealth = {
            status: 'online',
            latency: latencyMs,
            checkedAt: new Date(),
            source: source as 'local' | 'public' | undefined,
          };
        } else {
          const newFailures = prevFailures + 1;
          failureCountRef.current.set(device.id, newFailures);

          if (newFailures >= OFFLINE_THRESHOLD) {
            newHealth = { status: 'offline', checkedAt: new Date() };
          } else {
            // First blip — keep previous status to avoid flickering
            const prev = healthMapRef.current.get(device.id);
            newHealth = prev?.checkedAt
              ? { ...prev, checkedAt: new Date() }
              : { status: 'offline', checkedAt: new Date() };
          }
        }

        setHealthMap(prev => {
          const next = new Map(prev);
          next.set(device.id, newHealth);
          healthMapRef.current = next;
          return next;
        });
      })
    );

    setIsChecking(false);
  }, []);

  useEffect(() => {
    if (!isElectron()) return;
    intervalRef.current = setInterval(runCheck, interval);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runCheck, interval]);

  // Initial check as soon as devices load
  const deviceIds = useMemo(() => devices.map(d => d.id).join(','), [devices]);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!isElectron()) return;
    if (!deviceIds || firedRef.current) return;
    firedRef.current = true;
    runCheck();
  }, [deviceIds, runCheck]);

  return { healthMap, isChecking, checkNow: runCheck };
};
